const CHUNK_RECOVERY_SESSION_KEY = '__ts_chunk_recovery_attempted__';

/**
 * Webpack, browsers, and module loaders use several error shapes when a
 * content-hashed lazy asset no longer exists after a deploy.
 */
export function isChunkLoadError(error) {
  if (!error) return false;
  const name = error.name || '';
  const message = (error.message || String(error)) || '';
  return (
    name === 'ChunkLoadError'
    || /Loading chunk [\w-]+ failed/i.test(message)
    || /Loading CSS chunk [\w-]+ failed/i.test(message)
    || /Failed to fetch dynamically imported module/i.test(message)
    || /error loading dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message)
  );
}

function chunkRequestUrl(error, locationObject) {
  const explicitRequest = String(error?.request || error?.url || '').trim();
  const message = String(error?.message || '');
  const messageRequest = message.match(
    /\((?:missing|error|timeout):\s*([^)]+?\.(?:js|css)(?:[?#][^)]*)?)\)/i,
  )?.[1] || '';
  const request = explicitRequest || messageRequest;
  if (!request || !locationObject?.href) return '';

  try {
    const resolved = new URL(request, locationObject.href);
    if (resolved.origin !== locationObject.origin) return '';
    if (!/\.(?:js|css)$/i.test(resolved.pathname)) return '';
    return resolved.href;
  } catch (_) {
    return '';
  }
}

/**
 * `Cache-Control: immutable` can retain an HTML SPA fallback under a failed
 * chunk URL even after CacheStorage is cleared. A same-origin cache-reload
 * fetch repairs that browser HTTP-cache entry before the page reloads.
 */
export async function refreshFailedChunkHttpCache(error, options = {}) {
  const locationObject = options.locationObject !== undefined
    ? options.locationObject
    : (typeof window !== 'undefined' ? window.location : null);
  const fetchImpl = options.fetchImpl !== undefined
    ? options.fetchImpl
    : (typeof fetch === 'function' ? fetch : null);
  const requestUrl = chunkRequestUrl(error, locationObject);
  if (!requestUrl || typeof fetchImpl !== 'function') return false;

  try {
    const response = await fetchImpl(requestUrl, {
      cache: 'reload',
      credentials: 'same-origin',
    });
    const contentType = String(response?.headers?.get?.('content-type') || '');
    const expectsScript = /\.js(?:[?#]|$)/i.test(requestUrl);
    const expectsStylesheet = /\.css(?:[?#]|$)/i.test(requestUrl);
    const hasExpectedType = expectsScript
      ? /(?:java|ecma)script/i.test(contentType)
      : expectsStylesheet
        ? /text\/css/i.test(contentType)
        : false;
    if (!response?.ok || !hasExpectedType) return false;
    if (typeof response.arrayBuffer === 'function') await response.arrayBuffer();
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Wait briefly for a transiently unavailable lazy chunk, verifying that each
 * response is the expected executable asset before asking React to import it
 * again. This never reloads the page or touches application/student storage.
 */
export async function prepareFailedChunkRetry(error, options = {}) {
  const refresh = options.refresh || refreshFailedChunkHttpCache;
  const wait = options.wait || ((delayMs) => new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  }));
  const retryDelays = Array.isArray(options.retryDelays)
    ? options.retryDelays
    : [0, 300, 900, 1800];

  for (const delayMs of retryDelays) {
    if (Number(delayMs) > 0) await wait(Number(delayMs));
    try {
      if (await refresh(error, options)) return true;
    } catch (_) {
      // A later bounded attempt may succeed while a development/deploy build
      // finishes publishing the exact content-hashed asset.
    }
  }
  return false;
}

const prefetchRepairsInFlight = new Map();

/**
 * Repair a chunk failure that a background prefetch walked into.
 *
 * Prefetching happens without anyone asking for it — a pointer crossing the
 * sidebar is enough — so this path must never reload, never clear caches, and
 * never render an error. It only verifies that the asset is genuinely back and
 * warms the browser HTTP cache, then re-runs the import so the click that
 * follows resolves from the module cache instead of repeating the failure.
 * When the asset stays unavailable this resolves false and stays silent:
 * navigation still owns the visible retry/recovery path.
 */
export function repairPrefetchedChunk(error, retryLoad, options = {}) {
  if (!isChunkLoadError(error)) return Promise.resolve(false);

  const prepare = options.prepare || prepareFailedChunkRetry;
  const locationObject = options.locationObject !== undefined
    ? options.locationObject
    : (typeof window !== 'undefined' ? window.location : null);
  const inFlight = options.inFlight || prefetchRepairsInFlight;
  // One repair per failed asset. Several nav targets commonly share a vendor
  // chunk, so hovering across them would otherwise start the same bounded
  // retry sequence once per hover.
  const key = chunkRequestUrl(error, locationObject) || String(error?.message || 'chunk');
  const existing = inFlight.get(key);
  if (existing) return existing;

  const repair = Promise.resolve()
    .then(() => prepare(error, options))
    .then((repaired) => {
      if (!repaired || typeof retryLoad !== 'function') return Boolean(repaired);
      return Promise.resolve()
        .then(() => retryLoad())
        .then(() => true)
        .catch(() => false);
    })
    .catch(() => false)
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, repair);
  return repair;
}

/**
 * Remove only deploy/runtime caches, then reload the current application.
 * Student records, Firestore persistence, IndexedDB, and localStorage are not
 * touched. The session guard prevents a broken deploy from creating a reload
 * loop.
 */
export function recoverFromStaleCache(reason, options = {}) {
  const storage = options.sessionStorageObject !== undefined
    ? options.sessionStorageObject
    : (typeof window !== 'undefined' ? window.sessionStorage : null);
  const serviceWorkerContainer = options.serviceWorkerContainer !== undefined
    ? options.serviceWorkerContainer
    : (typeof navigator !== 'undefined' ? navigator.serviceWorker : null);
  const cacheStorage = options.cacheStorage !== undefined
    ? options.cacheStorage
    : (typeof caches !== 'undefined' ? caches : null);
  const reload = options.reload || (() => {
    if (typeof window !== 'undefined') window.location.reload();
  });
  const logger = options.logger !== undefined
    ? options.logger
    : (typeof console !== 'undefined' ? console : null);

  try {
    if (!options.force && storage?.getItem(CHUNK_RECOVERY_SESSION_KEY)) return false;
    storage?.setItem(CHUNK_RECOVERY_SESSION_KEY, String(Date.now()));
  } catch (_) {
    // Storage can be unavailable in private mode; recovery can still proceed.
  }

  try {
    logger?.warn?.('[recovery] Detected stale cache / chunk load failure, recovering.', reason);
  } catch (_) { /* diagnostics must never block recovery */ }

  const tasks = [];
  if (typeof serviceWorkerContainer?.getRegistrations === 'function') {
    tasks.push(
      Promise.resolve(serviceWorkerContainer.getRegistrations())
        .then((registrations) => Promise.all(
          (Array.isArray(registrations) ? registrations : [])
            .map((registration) => Promise.resolve(registration?.unregister?.()).catch(() => false)),
        ))
        .catch(() => []),
    );
  }
  if (typeof cacheStorage?.keys === 'function') {
    tasks.push(
      Promise.resolve(cacheStorage.keys())
        .then((keys) => Promise.all(
          (Array.isArray(keys) ? keys : [])
            .map((key) => Promise.resolve(cacheStorage.delete(key)).catch(() => false)),
        ))
        .catch(() => []),
    );
  }

  Promise.all(tasks)
    .then(() => refreshFailedChunkHttpCache(reason, options))
    .catch(() => false)
    .finally(() => {
      try { reload(); } catch (_) { /* a visible refresh control remains */ }
    });
  return true;
}

/**
 * Keep the old service worker (and its matching lazy chunks) active while the
 * user is working. Once the tab is hidden, activate the waiting worker and
 * reload only after it becomes the controller.
 */
export function scheduleWaitingServiceWorkerUpdate(registration, options = {}) {
  const waiting = registration?.waiting;
  const documentObject = options.documentObject !== undefined
    ? options.documentObject
    : (typeof document !== 'undefined' ? document : null);
  const serviceWorkerContainer = options.serviceWorkerContainer !== undefined
    ? options.serviceWorkerContainer
    : (typeof navigator !== 'undefined' ? navigator.serviceWorker : null);
  const reload = options.reload || (() => {
    if (typeof window !== 'undefined') window.location.reload();
  });

  if (!waiting || !documentObject) return () => {};

  let stopped = false;
  let activationRequested = false;
  let reloaded = false;

  const cleanup = () => {
    documentObject.removeEventListener?.('visibilitychange', requestActivationWhenHidden);
    serviceWorkerContainer?.removeEventListener?.('controllerchange', reloadAfterActivation);
  };
  const reloadAfterActivation = () => {
    if (stopped || !activationRequested || reloaded) return;
    reloaded = true;
    cleanup();
    try { reload(); } catch (_) { /* the next navigation will use the new worker */ }
  };
  const requestActivationWhenHidden = () => {
    if (stopped || activationRequested || documentObject.visibilityState !== 'hidden') return;
    activationRequested = true;
    serviceWorkerContainer?.addEventListener?.('controllerchange', reloadAfterActivation);
    try {
      waiting.postMessage({ type: 'SKIP_WAITING' });
    } catch (_) {
      // Leave the current worker/page intact. A later navigation can install
      // the update normally without risking any in-progress form state.
      activationRequested = false;
      serviceWorkerContainer?.removeEventListener?.('controllerchange', reloadAfterActivation);
    }
  };

  documentObject.addEventListener?.('visibilitychange', requestActivationWhenHidden);
  requestActivationWhenHidden();

  return () => {
    stopped = true;
    cleanup();
  };
}
